// Per-user theme settings: primary accent color + light/dark mode.
//
// Strategy: HSL tokens. We override --primary, --ring,
// --sidebar-primary, --sidebar-ring, and the accent-foreground at the
// :root level. Everything else (hover/border/text) derives from those
// in the existing tailwind preset, so a single color pick re-skins the
// app instantly.
//
// Persistence: localStorage. Survives reloads. Cleared on Reset.

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface ThemeColor {
  /** Internal id used for the preset grid. */
  id: string;
  /** Display name. */
  label: string;
  /** Primary accent in HSL components "h s% l%". */
  primary: string;
  /** Color shown in the picker swatch (CSS color or HSL string). */
  swatch: string;
}

export const THEME_PRESETS: ThemeColor[] = [
  { id: "indigo",  label: "Indigo",  primary: "243 75% 58%", swatch: "hsl(243 75% 58%)" },
  { id: "violet",  label: "Violet",  primary: "262 83% 58%", swatch: "hsl(262 83% 58%)" },
  { id: "blue",    label: "Blue",    primary: "221 83% 53%", swatch: "hsl(221 83% 53%)" },
  { id: "sky",     label: "Sky",     primary: "199 89% 48%", swatch: "hsl(199 89% 48%)" },
  { id: "teal",    label: "Teal",    primary: "173 80% 40%", swatch: "hsl(173 80% 40%)" },
  { id: "emerald", label: "Emerald", primary: "160 84% 39%", swatch: "hsl(160 84% 39%)" },
  { id: "amber",   label: "Amber",   primary: "38 92% 50%",  swatch: "hsl(38 92% 50%)"  },
  { id: "rose",    label: "Rose",    primary: "346 84% 56%", swatch: "hsl(346 84% 56%)" },
  { id: "slate",   label: "Slate",   primary: "215 20% 35%", swatch: "hsl(215 20% 35%)" },
];

const DEFAULT_PRESET = THEME_PRESETS[0]!;
const STORAGE_KEY = "torah:theme:v1";

type Mode = "light" | "dark";

interface StoredTheme {
  preset?: string; // preset id, or "custom"
  customHsl?: string; // "h s% l%" for custom
  mode?: Mode;
}

interface ThemeContextValue {
  presetId: string;
  primaryHsl: string;
  mode: Mode;
  setPreset: (id: string) => void;
  setCustomHsl: (hsl: string) => void;
  setMode: (mode: Mode) => void;
  reset: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function loadStored(): StoredTheme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") return parsed as StoredTheme;
    return {};
  } catch {
    return {};
  }
}

function saveStored(t: StoredTheme): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
  } catch {
    /* non-fatal */
  }
}

function applyTheme(primaryHsl: string, mode: Mode): void {
  const root = document.documentElement;
  root.style.setProperty("--primary", primaryHsl);
  root.style.setProperty("--ring", primaryHsl);
  root.style.setProperty("--sidebar-primary", primaryHsl);
  root.style.setProperty("--sidebar-ring", primaryHsl);
  if (mode === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<StoredTheme>(() => loadStored());

  const presetId = state.preset || DEFAULT_PRESET.id;
  const mode: Mode = state.mode ?? "light";

  const primaryHsl = useMemo(() => {
    if (presetId === "custom" && state.customHsl) return state.customHsl;
    const found = THEME_PRESETS.find((p) => p.id === presetId);
    return found?.primary ?? DEFAULT_PRESET.primary;
  }, [presetId, state.customHsl]);

  // Push to the DOM whenever something changes — including on mount.
  useEffect(() => {
    applyTheme(primaryHsl, mode);
  }, [primaryHsl, mode]);

  const setPreset = useCallback((id: string) => {
    setState((prev) => {
      const next = { ...prev, preset: id };
      saveStored(next);
      return next;
    });
  }, []);

  const setCustomHsl = useCallback((hsl: string) => {
    setState((prev) => {
      const next = { ...prev, preset: "custom", customHsl: hsl };
      saveStored(next);
      return next;
    });
  }, []);

  const setMode = useCallback((m: Mode) => {
    setState((prev) => {
      const next = { ...prev, mode: m };
      saveStored(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setState({});
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* non-fatal */
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{ presetId, primaryHsl, mode, setPreset, setCustomHsl, setMode, reset }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be inside ThemeProvider");
  return ctx;
}

// Helper: convert "#rrggbb" → "h s% l%" for stuffing into the
// --primary token. Returns null if input isn't a valid hex string.
export function hexToHsl(hex: string): string | null {
  const m = hex.trim().match(/^#?([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (!m) return null;
  let h = m[1]!;
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const light = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = light > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue = hue * 60;
  }
  return `${Math.round(hue)} ${Math.round(sat * 100)}% ${Math.round(light * 100)}%`;
}
